import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { PROD_BASE_URL, SAND_BASE_URL } from "../constant/env";
import OzAPIPath from "./ozApiPath";
import { authenticateOz, authenticateOz2 } from "./ozservices";

export const axiosInstance = axios.create({
  baseURL: PROD_BASE_URL,
});
export const sandboxInstance = axios.create({
  baseURL: SAND_BASE_URL,
});

const handleError = (
  err: unknown,
  refresh_token: () => void,
  handle_auth: () => void
): never => {
  const error = err as AxiosError;

  if (error.response) {
    const responseData = error.response.data as {
      error_code?: number;
      message?: string;
      [key: string]: any;
    };

    console.log("Refreshing Token");
    console.log({ responseData });

    if (responseData.error_code === 6) {
      handle_auth();
    }

    if (responseData.error_code === 8) {
      refresh_token();
    }

    console.error("Server Error:", responseData);
    throw new Error(responseData.message || "Server error occurred");
  } else if (error.request) {
    console.error("No response from server:", error.request);
    throw new Error("No response from server");
  } else {
    console.log("Unexpected Error:", error.message);
    throw new Error("Something went wrong");
  }
};

const handleTokenRefresh = async ({
  sandBox = false,
}: {
  sandBox: boolean;
}): Promise<boolean> => {
  const instance = sandBox ? sandboxInstance : axiosInstance;
  const refreshTokenName = sandBox ? "expire_token2" : "expire_token";
  const accessTokenName = sandBox ? "access_token2" : "access_token";
  console.log("Token Refreshing");
  try {
    const refreshToken = await getCookie(refreshTokenName);
    console.log({ refreshToken });
    if (!refreshToken) return false;
    const response = await instance.post(
      OzAPIPath.getApiPath("TOKEN_RENEWAL"),
      {
        expire_token: refreshToken,
      }
    );

    const newAccessToken = response.data.access_token;
    const newRefreshToken = response.data.expire_token;
    await setCookie(accessTokenName, newAccessToken);
    await setCookie(refreshTokenName, newRefreshToken);
    return true;
  } catch (error) {
    console.error("Token Refresh Failed", error);
    await deleteCookie(accessTokenName);
    await deleteCookie(refreshTokenName);
    return false;
  }
};

export const apiRequest = async <T>(
  config: AxiosRequestConfig,
  withAuth: boolean = true
): Promise<T> => {
  let triedRefresh = false;

  const sendRequest = async (): Promise<T> => {
    if (withAuth) {
      const token = await getCookie("access_token");
      if (token) {
        config.headers = {
          ...config.headers,
          "X-Forensic-Access-Token": token,
        };
      }
    }
    const response = await axiosInstance.request<T>(config);
    return response.data;
  };
  try {
    return await sendRequest();
  } catch (err) {
    let shouldRetry = false;
    handleError(
      err,
      async () => {
        if (!triedRefresh) {
          triedRefresh = true;
          const refreshed = await handleTokenRefresh({ sandBox: false });
          if (refreshed) {
            shouldRetry = true;
          }
        }
        if (shouldRetry) {
          return await sendRequest();
        }
      },
      async () => {
        if (!triedRefresh) {
          triedRefresh = true;
          const refreshed = await authenticateOz();
          if (refreshed) {
            shouldRetry = true;
          }
        }
        if (shouldRetry) {
          return await sendRequest();
        }
      }
    );
    throw new Error("Unhandled error in apiRequest");
  }
};
export const apiRequest2 = async <T>(
  config: AxiosRequestConfig,
  withAuth: boolean = true
): Promise<T> => {
  const sendRequest = async (): Promise<T> => {
    if (withAuth) {
      const token = await getCookie("access_token2");
      if (token) {
        config.headers = {
          ...config.headers,
          "X-Forensic-Access-Token": token,
        };
      }
    }
    const response = await sandboxInstance.request<T>(config);
    return response.data;
  };

  let triedRefresh = false;
  try {
    return await sendRequest();
  } catch (err) {
    let shouldRetry = false;
    handleError(
      err,
      async () => {
        console.log("Refreshing Token");
        if (!triedRefresh) {
          triedRefresh = true;
          const refreshed = await handleTokenRefresh({ sandBox: true });
          console.log({ refreshed });
          if (refreshed) {
            shouldRetry = true;
          }
        }
        if (shouldRetry) {
          return await sendRequest();
        }
      },

      async () => {
        if (!triedRefresh) {
          triedRefresh = true;
          const refreshed = await authenticateOz2();
          if (refreshed) {
            shouldRetry = true;
          }
        }
        if (shouldRetry) {
          return await sendRequest();
        }
      }
    );
    throw new Error("Unhandled error in apiRequest");
  }
};
